import { Check, CircleMinus, Clock, X } from "lucide-react-native";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import type { MedicationLog } from "../hooks/useMedicationLogs";
import { isMedicationLogForOccurrence } from "../medication-log-date";

export type DoseStatus = MedicationLog["status"];

export const DOSE_STATUS_COLORS: Record<DoseStatus, string> = {
  taken: "#82D4A8",
  late: "#F5C882",
  missed: "#E8927C",
  skipped: EMBER.colors.textLight,
};

export const DOSE_STATUS_ICONS: Record<DoseStatus, typeof Check> = {
  taken: Check,
  late: Clock,
  missed: X,
  skipped: CircleMinus,
};

function isDoseStatus(value: string | null | undefined): value is DoseStatus {
  return (
    value === "taken" ||
    value === "late" ||
    value === "missed" ||
    value === "skipped"
  );
}

function sameMedication(
  log: MedicationLog,
  medication: { medicationId?: string | null; medicationName?: string | null },
) {
  const normalizedName = medication.medicationName?.trim().toLowerCase();
  const medId = medication.medicationId ?? null;

  if (medId && log.patientMedicationId === medId) return true;
  if (
    normalizedName &&
    log.medicationName?.trim().toLowerCase() === normalizedName
  ) {
    return true;
  }
  return false;
}

/**
 * Find the latest dose-log entry for a medication row.
 *
 * Scheduled rows must match their exact scheduled occurrence. Legacy rows
 * without scheduled occurrence metadata fall back to today's medication log.
 */
export function getTodayDoseLog(
  logs: MedicationLog[] | undefined,
  medication: {
    medicationId?: string | null;
    medicationName?: string | null;
    scheduledFor?: string | null;
  },
  today: string,
  timezone?: string,
): MedicationLog | undefined {
  if (!logs || logs.length === 0) return undefined;

  const matches = logs.filter((log) => {
    if (!sameMedication(log, medication)) return false;
    return isMedicationLogForOccurrence(
      log,
      medication.scheduledFor,
      today,
      timezone,
    );
  });

  if (matches.length === 0) return undefined;

  return matches.reduce((a, b) =>
    new Date(a.takenAt).getTime() > new Date(b.takenAt).getTime() ? a : b,
  );
}

export function getTodayDoseStatus(
  logs: MedicationLog[] | undefined,
  medication: {
    medicationId?: string | null;
    medicationName?: string | null;
    scheduledFor?: string | null;
  },
  today: string,
  timezone?: string,
): DoseStatus | undefined {
  const latest = getTodayDoseLog(logs, medication, today, timezone);
  if (!latest) return undefined;
  return isDoseStatus(latest.status) ? latest.status : undefined;
}
